import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useShapefileTracking } from "../../../../hooks/useShapefileTracking";

export enum TRIGGER_MODES {
  BUTTON = "button",
  CONTEXT_MENU = "contextMenu"
}

export type TriggerMode = TRIGGER_MODES;

interface ContextMenuState {
  visible: boolean;
  position: { x: number; y: number };
}

export interface UseRightClickContextMenuReturn {
  triggerMode: TriggerMode;
  contextMenuState: ContextMenuState;
  onContextMenuClose: () => void;
}

export function useRightClickContextMenu() {
  const [contextMenuState, setContextMenuState] = useState<ContextMenuState>({
    visible: false,
    position: { x: 0, y: 0 }
  });

  const { trackContextMenuUsage } = useShapefileTracking();
  const nodeRef = useRef<HTMLElement>(null);

  const openContextMenu = useCallback(
    (event: React.MouseEvent | MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      trackContextMenuUsage("right-click-open");

      setContextMenuState({
        visible: true,
        position: { x: event.clientX, y: event.clientY }
      });
    },
    [trackContextMenuUsage]
  );

  // Checkbox context menu functionality
  useEffect(() => {
    if (!nodeRef.current) {
      return;
    }

    const findCheckbox = (): HTMLElement | null => {
      let currentElement: HTMLElement | null = nodeRef.current;

      // Walk up DOM tree to find the ant-tree-treenode container
      while (currentElement) {
        if (currentElement.className?.includes("ant-tree-treenode")) {
          // Found tree node, look for checkbox within it
          return currentElement.querySelector(".ant-tree-checkbox") as HTMLElement;
        }
        currentElement = currentElement.parentElement;
      }
      return null;
    };

    const bindCheckboxContextMenu = (): (() => void) | null => {
      const checkbox = findCheckbox();
      if (!checkbox) return null;

      const handleContextMenu = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        openContextMenu(e);
      };

      checkbox.addEventListener("contextmenu", handleContextMenu, {
        capture: true,
        passive: false
      });

      return () => {
        checkbox.removeEventListener("contextmenu", handleContextMenu, { capture: true });
      };
    };

    return bindCheckboxContextMenu();
  }, [openContextMenu]);

  const closeContextMenu = useCallback(() => {
    setContextMenuState((prev) => ({ ...prev, visible: false }));
  }, []);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && contextMenuState.visible) {
        closeContextMenu();
      }
    };

    if (contextMenuState.visible) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [contextMenuState.visible, closeContextMenu]);

  const contextMenuObject: UseRightClickContextMenuReturn = useMemo(
    () => ({
      triggerMode: TRIGGER_MODES.CONTEXT_MENU,
      contextMenuState,
      onContextMenuClose: closeContextMenu
    }),
    [contextMenuState, closeContextMenu]
  );

  return {
    contextMenuState,
    openContextMenu,
    closeContextMenu,
    contextMenuObject,
    nodeRef
  };
}
